import { AssetGroup } from '../core/asset/AssetGroup';
import { CircleAsset } from '../core/asset/CircleAsset';
import { ColorAsset } from '../core/asset/ColorAsset';
import { GradientAsset } from '../core/asset/GradientAsset';
import { ChartAsset } from '../core/asset/ChartAsset';
import { HtmlAsset } from '../core/asset/HtmlAsset';
import { IconAsset } from '../core/asset/IconAsset';
import { LottieAsset } from '../core/asset/LottieAsset/LottieAsset';
import { RectangleAsset } from '../core/asset/RectangleAsset';
import { TimeAwareMediaAsset } from '../core/asset/TimeAwareMediaAsset';
import type { GetAssetType, AssetType, Asset } from '../core/asset/BaseAsset';
import type Luminary from '../index';
import type { FetchResources } from '../types';
import { assertHasOwnProperties, assertInEnum, assertObject } from '../validators';
import { Config } from '../video-data/config';
import { TextAsset } from '../core/asset/TextAsset';
import { FancyCaptionAsset } from '../core/asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { Scene } from '../core/scene/Scene';
import type { Subscene } from '../core/scene/Subscene';

function getAssetForType<T extends AssetType>(
    assetType: T,
    parent: unknown,
    settings: object,
): GetAssetType<T> {
    const assetSettings = settings as never;
    switch (assetType) {
        case Config.GROUP_ASSET_TYPE:
            return new AssetGroup(parent, assetSettings) as GetAssetType<T>;
        case Config.COLOR_ASSET_TYPE:
            return new ColorAsset(parent as never, assetSettings) as GetAssetType<T>;
        case Config.CIRCLE_ASSET_TYPE:
            return new CircleAsset(parent, assetSettings) as GetAssetType<T>;
        case Config.GRADIENT_ASSET_TYPE:
            return new GradientAsset(parent as never, assetSettings) as GetAssetType<T>;
        case Config.CHART_ASSET_TYPE:
            return new ChartAsset(parent, assetSettings) as GetAssetType<T>;
        case Config.HTML_ASSET_TYPE:
            return new HtmlAsset(parent, assetSettings) as GetAssetType<T>;
        case Config.RECTANGLE_ASSET_TYPE:
            return new RectangleAsset(parent as never, assetSettings) as GetAssetType<T>;
        case Config.TEXT_ASSET_TYPE:
            return new TextAsset(parent, {
                ...settings,
            } as never) as GetAssetType<T>;
        case Config.FANCY_CAPTION_ASSET_TYPE:
            return new FancyCaptionAsset(parent, assetSettings) as GetAssetType<T>;
        case Config.MEDIA_ASSET_TYPE:
            return new TimeAwareMediaAsset(parent, {
                ...settings,
                audioMode: (parent as Asset).root.audioMode,
            } as never) as GetAssetType<T>;
        case Config.LOTTIE_ASSET_TYPE:
            return new LottieAsset(
                parent as never,
                {
                    ...settings,
                    enableCanvasLottie: (parent as Asset).root.enableCanvasLottie,
                } as never,
            ) as GetAssetType<T>;
        case Config.ICON_ASSET_TYPE:
            return new IconAsset(
                parent as never,
                {
                    ...settings,
                    enableCanvasLottie: (parent as Asset).root.enableCanvasLottie,
                } as never,
            ) as GetAssetType<T>;
        default:
            throw Error(`Unable to create asset ${assetType}`);
    }
}

export function addAssetToContainer<T extends AssetType>(
    parent: {
        autoDraw: boolean;
        fetchResources: FetchResources;
        insertAsset: (asset: Asset) => void;
        root: Luminary;
    },
    assetType: T,
    settings: object = {},
): GetAssetType<T> {
    assertInEnum(assetType, [
        Config.GROUP_ASSET_TYPE,
        Config.COLOR_ASSET_TYPE,
        Config.CIRCLE_ASSET_TYPE,
        Config.GRADIENT_ASSET_TYPE,
        Config.CHART_ASSET_TYPE,
        Config.RECTANGLE_ASSET_TYPE,
        Config.TEXT_ASSET_TYPE,
        Config.FANCY_CAPTION_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.LOTTIE_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
        Config.HTML_ASSET_TYPE,
    ]);
    const settingsWithDefaults = {
        assetNetworkManager: parent.root!._assetNetworkManager,
        reusableTextures: parent.root?._reusableTextures,
        timeline: parent.root.timeline,
        updateEventSource: parent,
        updateParentOnRedraw: true, // this should be true for all, except root nodes?
        autoDraw: parent.autoDraw,
        fetchResources: parent.fetchResources,
        getAspectRatio: () => parent.root.aspectRatio,
        seekWaitTime: parent.root.settings.video?.seekWaitTime ?? 2000,
        mediaSizeToUse: parent.root.mediaSizeToUse,
        getReferenceResolution: () => parent.root.referenceResolution,
        getOutputResolution: () => parent.root.outputResolution,
        ...settings,
    };
    const asset = getAssetForType(assetType, parent, settingsWithDefaults);

    // TODO: LUM-593 - assign this at creation because no reaction can see a new asset. Once BaseAsset children are
    // observable the scene can derive it from membership instead, and this goes away.
    const slide = asset.slide;
    if (slide && (Config.isTextAsset(asset) || Config.isFancyCaptionAsset(asset))) {
        asset.textDirection = slide.textDirection;
    }

    parent.insertAsset(asset);
    return asset;
}

export const buildChildrenForSceneUsingData = (
    slide: Scene | Subscene,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: Record<string, any>,
) => {
    assertObject(data);
    assertHasOwnProperties(data, ['children']);

    const scene = Config.isScene(slide) ? slide.scene : slide;
    const initAutoDraw = scene.autoDraw;
    scene.autoDraw = false;

    const childrenOfAsset: Record<string, Asset[]> = { scene: [] };
    const createdAssets: Record<string, Asset> = {};

    // Note that the order of when our components are created is crucial here.
    // We are iterating from leafs to root.
    // Before load data we have to append children to hierarchy
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.children.reverse().forEach((assetData: Record<string, any>) => {
        const settings = {
            assetNetworkManager: scene.root!._assetNetworkManager,
            reusableTextures: scene.root?._reusableTextures,
            timeline: scene.root.timeline,
            name: assetData.name,
            id: assetData.id,
            updateEventSource: scene,
            updateParentOnRedraw: true, // this should be true for all, except root nodes?
            autoDraw: false,
            fetchResources: scene.fetchResources,
            getAspectRatio: () => scene.root.aspectRatio,
            seekWaitTime: scene.root.settings.video?.seekWaitTime ?? 2000,
            mediaSizeToUse: scene.root.mediaSizeToUse,
            getReferenceResolution: () => scene.root.referenceResolution,
            getOutputResolution: () => scene.root.outputResolution,
        };
        const asset = getAssetForType(assetData.classType, scene, settings);

        if (childrenOfAsset[asset.id]) {
            childrenOfAsset[asset.id].forEach((child) => {
                asset.insertAsset(child as never);
            });
        }

        asset.load(assetData);
        createdAssets[assetData.id] = asset;

        const parentId = assetData.parentId || 'scene';
        childrenOfAsset[parentId] = childrenOfAsset[parentId] || [];
        childrenOfAsset[parentId].unshift(asset);
    });

    // append scene level children
    childrenOfAsset['scene'].forEach((child) => {
        scene.insertAsset(child);
    });

    // append scene level stickers
    if (Config.isScene(slide) && data.hasOwnProperty('stickers')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data.stickers.forEach((assetData: Record<string, any>) => {
            const settings = {
                assetNetworkManager: slide.stickers.root!._assetNetworkManager,
                reusableTextures: slide.stickers.root?._reusableTextures,
                timeline: slide.root.timeline,
                name: assetData.name,
                id: assetData.id,
                updateEventSource: slide.stickers,
                updateParentOnRedraw: true, // this should be true for all, except root nodes?
                autoDraw: false,
                fetchResources: slide.fetchResources,
                getAspectRatio: () => slide.root.aspectRatio,
                seekWaitTime: slide.root.settings.video?.seekWaitTime ?? 2000,
                mediaSizeToUse: slide.root.mediaSizeToUse,
                getReferenceResolution: () => slide.stickers.root.referenceResolution,
                getOutputResolution: () => slide.stickers.root.outputResolution,
            };
            const asset = getAssetForType(assetData.classType, slide.stickers, settings);
            asset.load(assetData);
            createdAssets[assetData.id] = asset;

            const parentId = assetData.parentId;
            if (parentId && createdAssets[parentId]) {
                createdAssets[parentId].insertAsset(asset);
            } else {
                slide.stickers.insertAsset(asset);
            }
        });
    }

    scene.autoDraw = initAutoDraw;
};
